// src/components/finance/FinancePeriods.jsx
import React, { useCallback, useEffect, useState } from 'react'
import { fetchFinancePeriods, createFinancePeriod, closeFinancePeriod, reopenFinancePeriod } from '../../api.js'
import FinanceNav from './FinanceNav.jsx'

export default function FinancePeriods() {
  const [rows, setRows] = useState([])
  const [err, setErr] = useState(null)
  const [code, setCode] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setErr(null)
    try {
      const data = await fetchFinancePeriods()
      setRows(data?.rows || [])
    } catch (e) {
      setErr(String(e?.message || e || 'Erreur chargement périodes'))
    }
  }, [])

  useEffect(() => { load() }, [load])

  async function create() {
    setBusy(true)
    try {
      await createFinancePeriod({ code, start_date: startDate, end_date: endDate })
      setCode('')
      setStartDate('')
      setEndDate('')
      await load()
    } catch (e) {
      setErr(String(e?.message || e || 'Erreur création période'))
    } finally {
      setBusy(false)
    }
  }

  async function close(id) {
    setBusy(true)
    try {
      await closeFinancePeriod(id)
      await load()
    } finally {
      setBusy(false)
    }
  }

  async function reopen(id) {
    setBusy(true)
    try {
      await reopenFinancePeriod(id)
      await load()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="p-6 space-y-6 min-h-screen">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Périodes comptables</h1>
        <div className="text-sm text-slate-500 mt-1">Ouvrir, clôturer et gérer les périodes.</div>
      </div>

      <FinanceNav />

      {err && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-800 text-sm">{err}</div>}

      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="text-xs text-slate-500 block mb-1 font-medium">Code</label>
            <input value={code} onChange={(e) => setCode(e.target.value)} className="border border-slate-200 px-3 py-2 rounded-lg w-full" placeholder="2026-02" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1 font-medium">Début</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="border border-slate-200 px-3 py-2 rounded-lg w-full" />
          </div>
          <div>
            <label className="text-xs text-slate-500 block mb-1 font-medium">Fin</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="border border-slate-200 px-3 py-2 rounded-lg w-full" />
          </div>
          <button onClick={create} disabled={busy} className="px-4 py-2 rounded-lg bg-slate-900 text-white">
            {busy ? '...' : 'Créer'}
          </button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 bg-slate-50 text-xs text-slate-500 font-semibold">
          <div className="col-span-2">Code</div>
          <div className="col-span-3">Début</div>
          <div className="col-span-3">Fin</div>
          <div className="col-span-2">Statut</div>
          <div className="col-span-2 text-right">Actions</div>
        </div>
        <div className="divide-y divide-slate-100">
          {rows.map((p) => (
            <div key={p.id} className="grid grid-cols-12 gap-3 px-4 py-3">
              <div className="col-span-2 text-sm font-semibold">{p.code}</div>
              <div className="col-span-3 text-sm">{String(p.start_date).slice(0, 10)}</div>
              <div className="col-span-3 text-sm">{String(p.end_date).slice(0, 10)}</div>
              <div className="col-span-2 text-sm">{p.status}</div>
              <div className="col-span-2 text-right">
                {p.status === 'open' ? (
                  <button onClick={() => close(p.id)} className="px-3 py-1.5 rounded bg-slate-900 text-white text-xs">Clôturer</button>
                ) : (
                  <button onClick={() => reopen(p.id)} className="px-3 py-1.5 rounded bg-slate-900 text-white text-xs">Réouvrir</button>
                )}
              </div>
            </div>
          ))}
          {!rows.length && (
            <div className="p-4 text-sm text-slate-500">Aucune période.</div>
          )}
        </div>
      </div>
    </div>
  )
}
